<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/holiday-pay-obligations-entries';
    import { computed, watch } from 'vue';
    import { Head, router } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import HolidayPayObligationsTableExport from '@/components/holiday-pay-obligations/HolidayPayObligationsTableExport.vue';
    import PageCard from '@/components/PageCard.vue';
    import PayrollSummary from '@/components/salary/PayrollSummary.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem, Company, HolidayPayObligation } from '@/types';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const { t } = useI18n();

    const { hasPermission } = usePermissions();

    const props = defineProps<{
        obligation: HolidayPayObligation;
        hasDepartments: number;
        company: Company;
    }>();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'holiday-pay-obligations.page_header',
            href: route('v2.salary.holiday-pay-obligations.index'),
        },
        {
            title: props.company.name,
            translated: true,
        },
    ];

    watch(selectedCompanyId, (newCompanyId, oldCompanyId) => {
        if (newCompanyId !== oldCompanyId && oldCompanyId !== undefined) {
            router.visit(route('v2.salary.holiday-pay-obligations.index'));
        }
    });

    const { isPending, data } = useQuery({
        queryKey: [
            QueryKey.HOLIDAY_PAY_OBLIGATIONS_ENTRIES,
            props.obligation.payroll_id,
            props.obligation.company_id,
        ],
        queryFn: getHolidayPayObligationEntries,
        placeholderData: [],
    });

    async function getHolidayPayObligationEntries() {
        const { data } = await axios.get(
            route('v2.salary.holiday-pay-obligations.entries.get', {
                companyId: selectedCompanyId.value,
                id: props.obligation.payroll_id,
            }),
        );
        return data.data;
    }
</script>

<template>
    <Head :title="t('holiday-pay-obligations.entries.page_header')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <div class="flex min-h-0 flex-1">
            <PageCard class="min-w-0 flex-1">
                <DataTable
                    :table-key="QueryKey.HOLIDAY_PAY_OBLIGATIONS_ENTRIES"
                    :columns="columns"
                    :data="data"
                    :loading="isPending"
                    :enable-search="false"
                    :enable-filters="false"
                    :default-column-visibility="{
                        id: false,
                        days_outstanding: !!props.hasDepartments,
                    }"
                    :default-sorting="[
                        {
                            id: 'employee_name',
                            desc: false,
                        },
                    ]"
                >
                    <template #title>
                        <CardTitle>
                            {{ t('holiday-pay-obligations.entries.page_header') }}
                        </CardTitle>
                    </template>
                    <template #actions>
                        <DataTableToolbarActions name="holiday-pay-obligations">
                            <HolidayPayObligationsTableExport
                                v-if="hasPermission(Permission.HOLIDAY_PAY_OBLIGATIONS_UPDATE)"
                                :holiday-pay-obligation="obligation"
                            />
                        </DataTableToolbarActions>
                    </template>
                </DataTable>
            </PageCard>

            <PayrollSummary
                :payroll="obligation"
                :show-badge="false"
            />
        </div>
    </AppLayout>
</template>
